import type { ReportDocument } from '@wyocspay/ui';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:4010';
const STORAGE_KEY = 'wyocspay.merchant.session';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/**
 * The session token lives in sessionStorage, not localStorage: it dies with
 * the tab. Combined with server-side revocation and a short TTL, a token left
 * behind on a shared machine stops being useful quickly.
 */
export function getToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}
export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(STORAGE_KEY, token);
  else sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Signal that the session is gone, so the app can return to sign-in.
 *
 * Clearing the token alone left whichever view was loading showing
 * "Authentication required" with no way forward.
 */
type UnauthorisedHandler = () => void;
let onUnauthorised: UnauthorisedHandler | null = null;

export function setUnauthorisedHandler(fn: UnauthorisedHandler | null): void {
  onUnauthorised = fn;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : {};

  if (!response.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    if (response.status === 401) {
      setToken(null);
      onUnauthorised?.();
    }
    throw new ApiError(response.status, err?.code ?? 'unknown', err?.message ?? 'Request failed.');
  }
  return body as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

/**
 * Fetch a binary response (a PDF) with the session token attached.
 *
 * The plain `request` helper reads the body as text and JSON-parses it, which
 * corrupts a PDF; and a bare `<a href>` cannot carry the Authorization header
 * the session uses. So a PDF is fetched here as a Blob, and the caller turns it
 * into an object URL to open or download.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (response.status === 401) {
      setToken(null);
      onUnauthorised?.();
    }
    let code = 'unknown';
    let message = 'Request failed.';
    try {
      const err = (JSON.parse(await response.text()) as { error?: { code?: string; message?: string } }).error;
      code = err?.code ?? code;
      message = err?.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, code, message);
  }
  return response.blob();
}

const qs = (params?: Record<string, string | number | boolean | undefined>): string => {
  if (!params) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export interface PublicPayment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  merchant_reference: string | null;
  description: string | null;
  payment_method_type: string | null;
  created_at: string;
  paid_at: string | null;
  amount_refunded: number;
  refund_status: string;
  dispute_status: string;
}

export interface Overview {
  merchant: {
    id: string;
    name: string;
    status: string;
    currency: string;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    requirements_due: string[];
    onboarding_complete: boolean;
    /** The platform's own margin. Not what a charge costs - see `rates`. */
    fee: { percent_bps: number; fixed: number };
  };
  /**
   * What a charge costs in total, and the parts it is made of.
   *
   * The panel shows `combined_*` only. Stripe's fee is deducted from the
   * connected account on top of the platform's, so the platform's figure alone
   * understates the cost by roughly three times, and the split is not a stable
   * number worth quoting: Stripe charges more for Amex, for international cards
   * and where a currency is converted. The per-payment view reads the real
   * deduction from Stripe.
   */
  rates: {
    platform_percent_bps: number;
    platform_fixed: number;
    processor_percent_bps: number;
    processor_fixed: number;
    combined_percent_bps: number;
    combined_fixed: number;
    currency: string;
  };
  /**
   * What charges have actually cost over the window, from settled fee
   * snapshots. When `charges` > 0 the panel shows these real figures instead
   * of the indicative `rates`. `effective_percent_bps` is the blended fee over
   * the volume that produced it.
   */
  fees: {
    actual_total: number;
    volume: number;
    charges: number;
    effective_percent_bps: number | null;
    currency: string;
  };
  stats: {
    volume_30d: number;
    payments_30d: number;
    succeeded_30d: number;
    success_rate: number | null;
    by_state: Record<string, number>;
  };
  recent: PublicPayment[];
}

export interface PaymentDetail {
  payment: PublicPayment;
  /** The card the customer paid with, the way Stripe reports it. */
  card?: {
    brand: string | null;
    last4: string | null;
    funding: string | null;
    country: string | null;
    category: string | null;
    /** apple_pay | google_pay | samsung_pay | link, when paid through a wallet. */
    wallet?: string | null;
  };
  channel?: string | null;
  card_present?: boolean;
  tap_to_pay?: boolean;
  /** 'ios' | 'android' for an in-person tap, else null - picks the Tap to Pay badge. */
  device_platform?: string | null;
  attempts: Array<{
    id: string;
    sequence: number;
    status: string;
    payment_method_type: string | null;
    failure_code: string | null;
    decline_code: string | null;
    failure_message: string | null;
    created_at: string;
    settled_at: string | null;
  }>;
  timeline: Array<{ at: string; action: string; actor: string; data: unknown }>;
}

/**
 * What a charge really cost.
 *
 * `status` is the discriminant. `unknown` means no snapshot exists yet: the
 * server returns only what the platform booked as its own fee, which is not the
 * total and must never be shown as the final cost. `estimated` means the
 * snapshot is still the up-front estimate, awaiting Stripe's settlement.
 * `settled` means Stripe's real deduction has been read back. Every amount is in
 * minor units. There is no client-side fee arithmetic - `difference` and
 * `merchant_net` come from the server already worked out.
 */
export interface PaymentFees {
  payment_id: string;
  status: 'settled' | 'estimated' | 'unknown';
  /** Only in the `unknown` case: the platform's own booked fee, not the total. */
  platform_fee_charged?: number | null;
  /** The presentment currency the fee figures are expressed in. */
  currency?: string;
  settlement_currency?: string | null;
  channel?: string | null;
  card?: { brand: string | null; country: string | null; category: string | null };
  estimated_total_fee: number | null;
  actual_total_fee: number | null;
  difference?: number | null;
  /** False when Stripe gave a total but no recoverable component split. */
  component_breakdown_available?: boolean;
  processor_fee?: number | null;
  platform_fee?: number | null;
  fx_fee?: number | null;
  merchant_net?: number | null;
  reconciled_at?: string | null;
}

/**
 * The cheapest accepted way this charge could have been taken.
 *
 * Charge-scoped and merchant-scoped: it prices only the methods this merchant
 * accepts and never recommends one the catalogue cannot price. Amounts are in
 * minor units; `savingsPercent` is a percentage. `recommended` is null when no
 * accepted method is priced.
 */
export interface MethodRecommendation {
  currency: string;
  channel: 'online' | 'card_present';
  baselineMethod: string;
  baselineFee: number | null;
  recommended: string | null;
  recommendedFee: number | null;
  savingsVsBaseline: number | null;
  savingsPercent: number | null;
  ranked: Array<{
    method: string;
    catalogMethod: string;
    totalFee: number;
    merchantNet: number;
    priced: boolean;
  }>;
  reason: string;
  consideredEnabledMethods: string[];
}

export interface Delivery {
  id: string;
  event_id: string;
  event_type: string;
  endpoint: string;
  http_status: number | null;
  attempts: number;
  status: string;
  created_at: string;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  error: string | null;
  is_replay: boolean;
}

export interface FeatureState {
  key: string;
  label: string;
  description: string;
  gate: 'open' | 'verification' | 'approval';
  available: boolean;
  blockedReason: string | null;
  /**
   * Whether asking could lead anywhere. Decided by the server, because one of
   * the reasons it cannot - Tap to Pay not being offered in this country - is a
   * list the panel has no business holding a copy of.
   */
  requestable: boolean;
  conditions: Record<string, unknown>;
  approvalRationale?: string;
  questions?: string[];
  request: { id: string; status: string; createdAt: string } | null;
}

export interface FeatureRequest {
  id: string;
  feature: string;
  feature_label: string;
  status: string;
  justification: string;
  intended_use: string;
  created_at: string;
  decision_note: string | null;
  conditions: Record<string, unknown> | null;
  messages: Array<{ id: string; author_role: string; body: string; created_at: string }>;
}

/**
 * One entry per account detail, whoever owns it.
 *
 * `route` is what the panel actually renders from: `request` gets a form,
 * `stripe` and `self` get a pointer to where the change really happens, and
 * `refer` gets an explanation of why there is no button. The server decides
 * which is which - see services/merchant-details.ts - so the panel cannot offer
 * a form for something it is not allowed to collect.
 */
export interface AccountDetailField {
  key: string;
  label: string;
  description: string;
  owner: 'platform' | 'stripe' | 'merchant';
  route: 'request' | 'refer' | 'stripe' | 'self';
  note: string;
  destination?: { view: string; tab?: string; label: string };
  input?: {
    type: 'text' | 'email' | 'currency';
    maxLength: number;
    placeholder?: string;
    allowEmpty?: boolean;
  };
  /** Null where the platform does not hold the value at all. */
  currentValue: string | null;
  pendingRequestId: string | null;
}

export interface AccountDetailRequest {
  id: string;
  field: string;
  field_label: string;
  status: string;
  current_value: string;
  requested_value: string;
  reason: string | null;
  created_at: string;
  decided_at: string | null;
  decision_note: string | null;
  applied_value: string | null;
}

export type SubscriptionTier = 'special' | 'vipplus';

export interface MerchantSubscription {
  id: string;
  tier: SubscriptionTier;
  status: 'PENDING_PAYMENT' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
  monthlyAmount: number;
  currency: string;
  currentPeriodEnd: string | null;
  autoDebit: boolean;
}

export interface SubscriptionApplication {
  id: string;
  tier: SubscriptionTier;
  status: 'PENDING' | 'INFO_REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  justification: string | null;
  infoRequest: string | null;
  adminAmount: number | null;
  currency: string;
  createdAt: string;
}

export interface PricingAnalysis {
  tier: SubscriptionTier;
  currency: string;
  monthlyPlatformFee: number;
  monthlyStripeFeeEst: number;
  floor: number;
  suggested: number;
  vipplusFullyEffective: boolean;
}

export interface SubscriptionOverview {
  current: MerchantSubscription | null;
  applications: SubscriptionApplication[];
  payUrl: string | null;
  estimate: { special: PricingAnalysis; vipplus: PricingAnalysis };
}

/** One fillable legal document, as the panel lists it. */
export interface DocumentSummary {
  key: string;
  title: string;
  description: string;
  category: string;
  /** Signing state: never issued (preview), issued and awaiting signature,
   *  accepted (signed), or declined. */
  state?: 'preview' | 'issued' | 'accepted' | 'declined';
  /** The issued running number, once issued. */
  number?: string | null;
  acceptedAt?: string | null;
  /** Why the current version was issued - `fee_change` means terms moved. */
  reason?: string | null;
  /** The merchant signs this document (draw or type), like the agreement. */
  signable?: boolean;
  /** Issued and sealed by the operator; the merchant only downloads it. */
  platformIssued?: boolean;
  /** Raised on demand by the merchant rather than at onboarding. */
  onDemand?: boolean;
  /** An internal operating record rather than a document the merchant signs. */
  internal?: boolean;
  /** Declining this document permanently closes the account (the agreement only). */
  declineCloses?: boolean;
}

export interface FormFieldOption {
  key: string;
  label: string;
}

/** A field the merchant fills in on a document (bank details, business profile). */
export interface FormField {
  token: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'number' | 'select' | 'radio';
  options?: FormFieldOption[];
  section?: string;
  placeholder?: string;
  help?: string;
  span?: 1 | 2;
}

export interface SignDocumentInput {
  name: string;
  title?: string;
  email?: string;
  /** The signature as a self-contained data: URL. */
  signature: string;
  method: 'handwritten' | 'electronic' | 'online';
}

export interface SealProof {
  number: string;
  signer: string;
  algorithm: string;
  sha256: string;
  signature: string;
  certificate: string;
  sealedAt: string | null;
  acceptedAt: string | null;
  verified: boolean;
}

export interface OnboardingData {
  company_registration_no?: string;
  tax_id?: string;
  vat?: string;
  dba?: string;
  representative_title?: string;
  representative_role?: string;
}

export interface OnboardingState {
  status: string;
  step: 'data' | 'review' | 'signing' | 'open';
  enforced: boolean;
  verified: boolean;
  missing: string[];
  documentCount: number;
  rejectionReason: string | null;
  data: Required<OnboardingData>;
  mandatory: Array<{ key: string; title: string; signed: boolean }>;
}

export interface OnboardingDoc {
  id: string;
  kind: string;
  label: string | null;
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
}

export interface InvoiceBillTo {
  name: string | null;
  email: string | null;
  address: string | null;
  tax_id: string | null;
}

export interface InvoiceLineRow {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface Invoice {
  id: string;
  kind: 'invoice' | 'credit_note';
  number: string;
  status: string;
  locked: boolean;
  currency: string;
  customer_id: string | null;
  bill_to: InvoiceBillTo;
  lines: InvoiceLineRow[];
  subtotal: number;
  discount_bps: number;
  discount_amount: number;
  tax_label: string | null;
  tax_bps: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  outstanding: number;
  credited_amount: number;
  credits_invoice_id: string | null;
  po_reference: string | null;
  notes: string | null;
  payment_id: string | null;
  issued_at: string | null;
  due_at: string | null;
  sent_at: string | null;
  paid_at: string | null;
  reminded_at: string | null;
  reminder_count: number;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceMeta {
  default_currency: string;
  currencies: { code: string; name: string; symbol: string }[];
  pdf_available: boolean;
}

export interface InvoiceLineInput {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface InvoiceInput {
  customer_id?: string | null;
  currency?: string | null;
  bill_to?: InvoiceBillTo | null;
  lines?: InvoiceLineInput[];
  discount_bps?: number;
  tax_label?: string | null;
  tax_bps?: number;
  due_at?: string | null;
  due_days?: number;
  po_reference?: string | null;
  notes?: string | null;
}

export const api = {
  invite: (token: string) =>
    request<{ email: string; role: string; merchant: { name: string }; expires_at: string }>(
      `/auth/invite/${token}`,
    ),
  acceptInvite: (token: string, name: string, password: string) =>
    post<{ ok: boolean; email: string }>(`/auth/invite/${token}/accept`, { name, password }),

  connectRefresh: () =>
    post<{
      charges_enabled: boolean;
      payouts_enabled: boolean;
      details_submitted: boolean;
      requirements_due: string[];
      eventually_due: string[];
      past_due: string[];
      pending_verification: string[];
      disabled_reason: string | null;
      unlocked: string[];
      /** Stripe's verdict per capability, so nothing here has to be guessed at. */
      capabilities: Record<string, CapabilityReport>;
      missing_capabilities: string[];
      blocked_reason: string | null;
    }>('/panel/connect/refresh'),

  /**
   * Ask Stripe for the capabilities this account should have and does not.
   *
   * Separate from `connectRefresh`, which only reads. This writes to the
   * connected account, so it is a button somebody presses rather than something
   * that happens on a timer.
   */
  requestCapabilities: () =>
    post<{
      changed: boolean;
      requested: string[];
      already_requested: string[];
      refused: Array<{ capability: string; reason: string }>;
      newly_due: string[];
      charges_enabled: boolean;
      requirements_due: string[];
      missing_capabilities: string[];
    }>('/panel/connect/capabilities'),

  paymentLinks: (id: string) => request<{ data: PaymentLinkRow[] }>(`/panel/payments/${id}/links`),
  paymentLink: (id: string, mode: 'renew' | 'reissue') =>
    post<{ mode: string; url: string | null; expires_at: string; note: string }>(
      `/panel/payments/${id}/link`,
      { mode },
    ),
  /**
   * Stop a link's URL working, for good.
   *
   * Irreversible: the customer holding that URL gets "this payment link has
   * been cancelled" and there is no undo. The panel confirms before calling it.
   */
  revokePaymentLink: (paymentId: string, linkId: string) =>
    post<{ ok: boolean; payment_id: string; link_id: string }>(
      `/panel/payments/${paymentId}/links/${linkId}/revoke`,
    ),

  /**
   * Issue a link and email it, in one call.
   *
   * There is no "send this URL" endpoint and there will not be one: the URL is
   * minted server-side from the payment, so the panel cannot be talked into
   * mailing something it was handed. The URL comes back for copying too.
   */
  emailPaymentLink: (paymentId: string, body: { to: string; note?: string }) =>
    post<{ sent: boolean; to: string; url: string; expires_at: string; message_id: string | null }>(
      `/panel/payments/${paymentId}/link/email`,
      body,
    ),

  terminals: () => request<TerminalsPage>('/panel/terminals'),

  /** The values a merchant needs to register their shop with their tax authority. */
  taxRegistration: () => request<TaxRegistrationPage>('/panel/tax-registration'),

  /** Everything that has gone wrong on one terminal, newest first. */
  terminalProblems: (id: string) =>
    request<TerminalProblemsPage>(`/panel/terminals/${encodeURIComponent(id)}/problems`),
  /**
   * Register a phone by the code it is displaying.
   *
   * There is no invitation to send and no password to set on the device: the
   * phone shows a code, this claims it. See services/terminals.ts for why that
   * is the stronger arrangement rather than the lazier one.
   */
  claimTerminal: (body: { code: string; label: string }) =>
    post<{ terminalId: string; label: string }>('/panel/terminals/claim', body),
  removeTerminal: (id: string, reason?: string) =>
    request<{ removed: boolean }>(`/panel/terminals/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  readers: () => request<ReadersPage>('/panel/readers'),
  /**
   * Register a Stripe reader by the pairing code on its own screen.
   *
   * The code is Stripe's, not ours: it is generated by the device under
   * Settings and exchanged with Stripe by our server. Nothing about this device
   * ever authenticates to WyoCSPay, which is why there is no token to collect
   * afterwards the way there is with a phone.
   */
  registerReader: (body: { registration_code: string; label: string }) =>
    post<ReaderRow>('/panel/readers', body),
  renameReader: (id: string, label: string) =>
    request<ReaderRow>(`/panel/readers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    }),
  removeReader: (id: string, reason?: string) =>
    request<{ removed: boolean }>(`/panel/readers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  startReaderPayment: (
    id: string,
    body: {
      amount: number;
      currency?: string;
      description?: string;
      reference?: string;
      /** Gratuity in minor units, agreed in the panel before the reader starts. */
      tip?: number;
      idempotency_key: string;
    },
  ) => post<ReaderPaymentStarted>(`/panel/readers/${id}/payments`, body),
  readerPaymentProgress: (id: string, paymentId: string) =>
    request<ReaderPaymentProgress>(`/panel/readers/${id}/payments/${paymentId}`),
  cancelReaderAction: (id: string, paymentId?: string) =>
    post<{ cancelled: boolean; action: ReaderAction }>(`/panel/readers/${id}/cancel`, {
      payment_id: paymentId,
    }),

  mailSettings: () => request<MailSettings>('/panel/mail'),
  saveMailSettings: (body: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password?: string;
    from_name: string;
    from_email: string;
    reply_to?: string | null;
  }) => request<MailSettings>('/panel/mail', { method: 'PUT', body: JSON.stringify(body) }),
  testMail: (to: string) => post<{ ok: boolean; messageId: string | null }>('/panel/mail/test', { to }),
  disconnectMail: () => request<{ disconnected: boolean }>('/panel/mail', { method: 'DELETE' }),
  mailDeliveries: () => request<{ data: MailDelivery[] }>('/panel/mail/deliveries'),

  // Invoicing
  invoiceMeta: () => request<InvoiceMeta>('/panel/invoices/meta'),
  invoices: (params?: { status?: string; kind?: string; customer_id?: string; overdue?: boolean }) =>
    request<{ invoices: Invoice[] }>(`/panel/invoices${qs(params)}`),
  invoice: (id: string) => request<{ invoice: Invoice }>(`/panel/invoices/${id}`),
  createInvoice: (body: InvoiceInput) => post<{ invoice: Invoice }>('/panel/invoices', body),
  updateInvoice: (id: string, body: InvoiceInput) =>
    request<{ invoice: Invoice }>(`/panel/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  invoiceStatus: (id: string, status: 'sent' | 'cancelled' | 'draft') =>
    post<{ invoice: Invoice }>(`/panel/invoices/${id}/status`, { status }),
  recordInvoicePayment: (
    id: string,
    body: { amount: number; method?: string; reference?: string; paid_at?: string },
  ) => post<{ invoice: Invoice }>(`/panel/invoices/${id}/payments`, body),
  createCreditNote: (id: string, body?: { lines?: InvoiceLineInput[]; notes?: string }) =>
    post<{ invoice: Invoice }>(`/panel/invoices/${id}/credit-note`, body ?? {}),
  refreshOverdueInvoices: () => post<{ changed: number }>('/panel/invoices/refresh-overdue'),
  invoicePayLink: (id: string) => post<{ url: string; expires_at: string }>(`/panel/invoices/${id}/pay-link`),
  sendInvoice: (
    id: string,
    body: { to?: string; include_pay_link?: boolean; reminder?: boolean; note?: string },
  ) => post<{ invoice: Invoice }>(`/panel/invoices/${id}/send`, body),
  invoicePdfBlob: (id: string) => fetchBlob(`/panel/invoices/${id}/pdf`),

  features: () => request<{ data: FeatureState[] }>('/panel/features'),
  machinePayments: () => request<MachineAccessState>('/panel/machine-payments'),
  requestMachinePayments: () => post<MachineAccessState>('/panel/machine-payments/request'),
  machineResources: () => request<{ data: MachineResource[] }>('/panel/machine-payments/resources'),
  createMachineResource: (body: {
    slug: string; name: string; description?: string | null; upstream_url?: string | null;
    method?: 'GET' | 'POST'; protocol?: 'x402' | 'mpp' | 'both'; price_minor: number;
    currency?: string; status?: 'DRAFT' | 'ACTIVE' | 'PAUSED'; livemode?: boolean;
  }) => post<MachineResource>('/panel/machine-payments/resources', body),
  machineKeys: () => request<{ data: Array<{ id: string; name: string; prefix: string; scopes: string[]; livemode: boolean; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }> }>('/panel/machine-payments/keys'),
  createMachineKey: (body: { name: string; scopes: string[]; livemode: boolean }) => post<{ id: string; key: string; prefix: string; scopes: string[]; livemode: boolean }>('/panel/machine-payments/keys', body),
  provisionMachineDepositAddress: (body: { network: 'tempo' | 'base' | 'solana'; livemode: boolean }) => post<{ address: string; network: string; connectedAccountId: string }>('/panel/machine-payments/deposit-address', body),
  featureRequests: () => request<{ data: FeatureRequest[] }>('/panel/feature-requests'),
  requestFeature: (body: {
    feature: string;
    justification: string;
    intended_use: string;
    expected_volume?: string;
  }) => post<{ id: string }>('/panel/feature-requests', body),
  replyToRequest: (id: string, body: string) =>
    post<{ ok: boolean }>(`/panel/feature-requests/${id}/reply`, { body }),

  // --- Fee-waiver packages (subscriptions) ---
  subscription: () => request<SubscriptionOverview>('/panel/subscription'),
  requestSubscription: (body: { tier: SubscriptionTier; justification?: string; volume?: string }) =>
    post<SubscriptionApplication>('/panel/subscription-applications', body),
  cancelSubscriptionApplication: (id: string) =>
    post<SubscriptionApplication>(`/panel/subscription-applications/${id}/cancel`, {}),
  setSubscriptionAutoDebit: (enabled: boolean) =>
    post<MerchantSubscription>('/panel/subscription/auto-debit', { enabled }),

  // --- Account details ---

  accountDetails: () =>
    request<{ fields: AccountDetailField[]; requests: AccountDetailRequest[] }>('/panel/account-details'),
  requestDetailChange: (body: { field: string; requested_value: string; reason?: string }) =>
    post<{ id: string }>('/panel/account-details/requests', body),
  withdrawDetailRequest: (id: string) =>
    post<{ ok: boolean }>(`/panel/account-details/requests/${id}/withdraw`),

  // --- EU platform reporting (DAC7) ---
  //
  // Council Directive (EU) 2021/514. Whether it binds this platform at all is
  // unresolved - see compliance/dac7-reporting.md - so nothing here is required
  // of a merchant by the panel; it is offered and explained.

  dac7: () =>
    request<{
      in_scope: boolean;
      signals: string[];
      profile: Dac7Profile | null;
      missing: Dac7MissingField[];
      due_diligence_valid: boolean;
      current_year: number;
      deadline: string;
      due_diligence_deadline: string;
      guidance: Dac7Guidance;
    }>('/panel/dac7'),

  saveDac7: (body: Dac7Input) =>
    post<{ profile: Dac7Profile; missing: Dac7MissingField[] }>('/panel/dac7', body),

  dac7Aggregate: (year: number) =>
    request<{ aggregate: Dac7Aggregate }>(`/panel/dac7/aggregate?year=${year}`),

  login: (email: string, password: string) =>
    post<{
      token: string;
      /**
       * PCI DSS 8.3.9 - a merchant account is protected by a password alone, so
       * its password expires after ninety days and the session can do nothing
       * until a new one is set.
       */
      password_change_required: boolean;
      user: { name: string; email: string; merchant_id: string };
    }>('/auth/login', { email, password, scope: 'merchant' }),
  logout: () => post<{ ok: boolean }>('/auth/logout'),
  me: () =>
    request<{
      user: {
        id: string;
        name: string;
        email: string;
        merchant_id: string;
        /** Drives date and money formatting, and comes from the business, not the browser. */
        country: string | null;
        default_currency: string | null;
        /** What Stripe actually settles this account in. */
        settlement_currency: string | null;
      };
      session: { password_change_required: boolean };
    }>('/auth/me'),

  /** Succeeds by ending every session, this one included. Sign in again after. */
  changePassword: (current_password: string, new_password: string) =>
    post<{ ok: boolean; sessions_terminated: number; sign_in_again: boolean }>('/auth/password', {
      current_password,
      new_password,
    }),

  overview: () => request<Overview>('/panel/overview'),
  payments: (params: { state?: string; search?: string }) => {
    const q = new URLSearchParams();
    if (params.state && params.state !== 'all') q.set('state', params.state);
    if (params.search) q.set('search', params.search);
    return request<{ data: PublicPayment[] }>(`/panel/payments?${q}`);
  },
  payment: (id: string) => request<PaymentDetail>(`/panel/payments/${id}`),
  /** What this charge really cost: estimate, settled actual and the difference. */
  paymentFees: (id: string) => request<PaymentFees>(`/panel/payments/${id}/fees`),
  /** The cheapest accepted way this charge could have been taken. */
  recommendedMethod: (id: string) =>
    request<MethodRecommendation>(`/panel/payments/${id}/recommended-method`),
  createPayment: (body: { amount: number; currency: string; description?: string; merchant_reference?: string }) =>
    post<PublicPayment & { payment_url: string }>('/panel/payments', body),

  balance: () => request<{ available: Array<{ amount: number; currency: string }>; pending: Array<{ amount: number; currency: string }> }>('/panel/balance'),
  payouts: () => request<{ data: Array<{ id: string; amount: number; currency: string; status: string; arrival_date: string }> }>('/panel/payouts'),

  connectSession: () =>
    post<{
      client_secret: string;
      publishable_key: string;
      account_id: string;
      platform_controlled: boolean;
    }>('/panel/connect/session'),

  refund: (id: string, body: { amount?: number; reason?: string }) =>
    post<{ refund: { id: string; amount: number; remaining: number; status: string | null } }>(
      `/panel/payments/${id}/refund`,
      body,
    ),
  refunds: (id: string) =>
    request<{
      data: Array<{
        id: string;
        amount: number;
        currency: string;
        status: string | null;
        reason: string | null;
        created: string;
        requested_by: string | null;
      }>;
    }>(`/panel/payments/${id}/refunds`),

  apiKeys: () => request<{ data: Array<{ id: string; name: string; prefix: string; livemode: boolean; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }> }>('/panel/api-keys'),
  createApiKey: (name: string, livemode: boolean) =>
    post<{ id: string; key: string; prefix: string; livemode: boolean }>('/panel/api-keys', { name, livemode }),
  revokeApiKey: (id: string) => request<{ ok: boolean }>(`/panel/api-keys/${id}`, { method: 'DELETE' }),

  endpoints: () => request<{ data: Array<{ id: string; url: string; status: string; active_secrets: number; rotation_in_progress: boolean; created_at: string }> }>('/panel/webhooks/endpoints'),
  createEndpoint: (url: string, description?: string) =>
    post<{ id: string; secret: string }>('/panel/webhooks/endpoints', { url, description }),
  rotateSecret: (id: string, overlapSeconds: number) =>
    post<{ secret: string; old_secret_expires_at: string | null }>(
      `/panel/webhooks/endpoints/${id}/rotate`,
      { overlap_seconds: overlapSeconds },
    ),

  deliveries: () => request<{ data: Delivery[] }>('/panel/webhooks/deliveries'),
  replayDelivery: (id: string) => post<{ id: string }>(`/panel/webhooks/deliveries/${id}/replay`),

  activity: () => request<{ data: Array<{ at: string; action: string; actor: string; subject: string; data: unknown }> }>('/panel/activity'),

  // --- payment methods ---

  paymentMethods: () => request<MethodList>('/panel/payment-methods'),
  updatePaymentMethods: (changes: Record<string, boolean>) =>
    post<{ configuration_id: string }>('/panel/payment-methods', { changes }),

  // --- branding ---

  /**
   * The logo shown to this merchant's own customers on the payment page.
   *
   * Optional from end to end. A merchant who never touches this has a payment
   * page identical to the one they had before logos existed, which is why
   * nothing in the panel treats a missing logo as an outstanding task.
   */
  logo: () => request<LogoState>('/panel/logo'),
  setLogo: (image: string) => post<LogoState>('/panel/logo', { image }),
  removeLogo: () => request<LogoState>('/panel/logo', { method: 'DELETE' }),
  /** Answers the onboarding prompt with "not now", once and for good. */
  skipLogo: () => post<LogoState>('/panel/logo/skip'),

  /**
   * The logo bytes.
   *
   * Not `request`, because the response is binary rather than JSON and the
   * endpoint needs the bearer token that a plain <img src> would not send.
   */
  logoImage: async (): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/panel/logo/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new ApiError(response.status, 'logo_failed', 'Could not load the logo.');
    return response.blob();
  },

  // --- support tickets ---

  tickets: (status = 'all') =>
    request<{ data: TicketSummary[]; counts: Record<string, number> }>(
      `/panel/tickets?status=${encodeURIComponent(status)}`,
    ),
  ticket: (id: string) => request<TicketThread>(`/panel/tickets/${id}`),
  createTicket: (body: {
    subject: string;
    body: string;
    category: string;
    priority: string;
    attachments: Array<{ filename: string; content: string }>;
  }) => post<{ id: string; number: string }>('/panel/tickets', body),
  replyToTicket: (
    id: string,
    body: { body: string; attachments: Array<{ filename: string; content: string }> },
  ) => post<{ id: string; status: string }>(`/panel/tickets/${id}/reply`, body),
  setTicketStatus: (id: string, status: 'RESOLVED' | 'OPEN') =>
    post<{ status: string }>(`/panel/tickets/${id}/status`, { status }),

  // --- reporting and export ---
  //
  // Everything below is this merchant's own account and nothing else. There is
  // no merchant identifier in any of these calls: the server takes it from the
  // session through `merchantIdOf` and would have no way to honour one sent
  // from here.

  reportMeta: () =>
    request<{
      merchant_id: string;
      fee_lookup_default: number;
      fee_lookup_max: number;
      max_period_days: number;
      sections: Array<{ key: string; label: string }>;
    }>('/panel/reports/meta'),

  report: (query: ReportQuery) =>
    request<ReportDocument>(`/panel/reports/account?${reportParams(query, 'json')}`),

  /**
   * The same report as a file.
   *
   * Fetched with the bearer token rather than opened as a link, for the reason
   * the ticket attachments are: an anchor cannot carry an Authorization header,
   * and a URL that yields a merchant's whole ledger has no business sitting in
   * browser history where the next person to use the machine can find it.
   */
  reportFile: async (query: ReportQuery, format: 'csv' | 'xlsx' | 'pdf', table?: string): Promise<Blob> => {
    const token = getToken();
    const params = reportParams(query, format);
    const response = await fetch(
      `${BASE}/panel/reports/account?${params}${table ? `&table=${encodeURIComponent(table)}` : ''}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      throw new ApiError(response.status, 'report_failed', 'The report could not be produced.');
    }
    return response.blob();
  },

  /** The printable document, as markup, for the panel to open and print. */
  reportHtml: async (query: ReportQuery): Promise<string> => {
    const token = getToken();
    const response = await fetch(`${BASE}/panel/reports/account?${reportParams(query, 'html')}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'report_failed', 'The report could not be produced.');
    }
    return response.text();
  },

  /**
   * Attachment bytes.
   *
   * Not `request`, because the response is binary rather than JSON and the
   * endpoint needs the bearer token that a plain <a href> would not send.
   */
  ticketAttachment: async (id: string): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/panel/tickets/attachments/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new ApiError(response.status, 'attachment_failed', 'Could not load the attachment.');
    return response.blob();
  },

  // --- documents ---
  //
  // A generic set of fillable legal documents, auto-filled from this merchant's
  // live data. `documents()` lists them; `documentHtml()` fetches one rendered
  // to self-contained HTML.

  documents: () => request<{ documents: DocumentSummary[] }>('/panel/documents'),

  /** The fillable fields a document asks the merchant to complete, plus current values. */
  documentForm: (key: string) =>
    request<{ fields: FormField[]; values: Record<string, string> }>(
      `/panel/documents/${encodeURIComponent(key)}/form`,
    ),
  /** Save the merchant's answers to a document's fillable fields. */
  saveDocumentForm: (key: string, values: Record<string, string>) =>
    post<{ ok: true; values: Record<string, string> }>(
      `/panel/documents/${encodeURIComponent(key)}/form`,
      values,
    ),

  /** Sign a document into an Electronic Acceptance Record. */
  signDocument: (key: string, body: SignDocumentInput) =>
    post<{ ok: true; number: string; accepted_at: string | null }>(
      `/panel/documents/${encodeURIComponent(key)}/sign`,
      body,
    ),

  /** Decline a document. For the agreement this permanently closes the account. */
  declineDocument: (key: string) =>
    post<{ ok: true }>(`/panel/documents/${encodeURIComponent(key)}/decline`),

  /** The cryptographic seal + certificate for a signed document, for verification. */
  documentSeal: (key: string) =>
    request<SealProof>(`/panel/documents/${encodeURIComponent(key)}/seal`),

  /** The signed document as a PAdES-signed PDF (binary). */
  documentSignedPdf: async (key: string): Promise<Blob> => {
    const token = getToken();
    const res = await fetch(`${BASE}/panel/documents/${encodeURIComponent(key)}/signed-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let message = 'The signed PDF could not be generated.';
      try {
        const body = (await res.json()) as { error?: { message?: string } };
        if (body.error?.message) message = body.error.message;
      } catch {
        /* keep default */
      }
      throw new ApiError(res.status, 'pdf_failed', message);
    }
    return res.blob();
  },

  // --- Onboarding / KYC gate ---
  onboarding: () => request<OnboardingState>('/panel/onboarding'),
  saveOnboardingData: (body: OnboardingData) =>
    post<{ ok: true }>('/panel/onboarding/data', body),
  onboardingDocuments: () =>
    request<{ documents: OnboardingDoc[] }>('/panel/onboarding/documents'),
  uploadOnboardingDocument: (body: {
    kind: string;
    label?: string;
    filename: string;
    mime_type: string;
    data: string;
  }) => post<{ ok: true; id: string }>('/panel/onboarding/documents', body),
  deleteOnboardingDocument: (id: string) =>
    request<{ ok: true }>(`/panel/onboarding/documents/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  submitOnboarding: () => post<{ ok: true }>('/panel/onboarding/submit'),

  /**
   * One document as self-contained markup, for the panel to load into an iframe.
   *
   * Fetched with the bearer token and handed to the iframe as `srcdoc`, rather
   * than pointing the frame at the URL: an iframe cannot carry an Authorization
   * header, and this route is session-authed like everything else here. Returns
   * text, not JSON.
   */
  documentHtml: async (key: string): Promise<string> => {
    const token = getToken();
    const response = await fetch(`${BASE}/panel/documents/${encodeURIComponent(key)}/render`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'document_failed', 'The document could not be loaded.');
    }
    return response.text();
  },

  // The business-identity fields the legal documents need that no upstream
  // system yields (tax id, company registration number, trading name, the
  // representative's title). Read and set here; auto-filled onto every document.
  documentDetails: () => request<DocumentDetails>('/panel/document-details'),

  saveDocumentDetails: (body: DocumentDetails) =>
    request<{ ok: true }>('/panel/document-details', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export interface DocumentDetails {
  tax_id: string;
  company_registration_no: string;
  dba: string;
  representative_title: string;
}

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

export type ReportSection = 'all' | 'transactions' | 'fees' | 'refunds' | 'payouts';

export interface ReportQuery {
  /** Calendar days, inclusive at both ends, read as UTC by the server. */
  from: string;
  to: string;
  section: ReportSection;
  /** How many payments the real Stripe fee is looked up for. */
  feeLimit?: number;
}

function reportParams(query: ReportQuery, format: string): string {
  const params = new URLSearchParams({
    from: query.from,
    to: query.to,
    section: query.section,
    format,
  });
  if (query.feeLimit) params.set('fee_limit', String(query.feeLimit));
  return params.toString();
}

/**
 * Saving a file the browser already holds.
 *
 * An object URL rather than a link to the API, so the download carries the
 * bytes that were fetched with the session token instead of asking the browser
 * to fetch them again without one. Revoked immediately: the URL is a handle on
 * a merchant's ledger held in memory, and there is no reason for it to outlive
 * the click.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface LogoState {
  present: boolean;
  mime_type: string | null;
  size: number | null;
  sha256: string | null;
  updated_at: string | null;
  /** Whether onboarding has already asked for one. */
  prompted: boolean;
}

export interface TicketSummary {
  id: string;
  number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  messages: number;
  created_at: string;
  last_message_at: string;
}

export interface TicketAttachmentMeta {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  inline: boolean;
}

export interface TicketMessage {
  id: string;
  author_name: string;
  author_side: string;
  body: string;
  internal: boolean;
  created_at: string;
  attachments: TicketAttachmentMeta[];
}

export interface TicketThread extends Omit<TicketSummary, 'messages'> {
  messages: TicketMessage[];
  resolved_at: string | null;
  closed_at: string | null;
}

/**
 * Who has to act before a payment method can run.
 *
 * `platform` is the one that changes how a row is written: it means WyoCSPay
 * never asked Stripe for the capability, which is not the merchant's problem
 * and must not be shown to them as one.
 */
export type CapabilityOwner = 'platform' | 'merchant' | 'stripe' | 'unavailable' | 'ready';

export interface CapabilityReport {
  status: string;
  active: boolean;
  owner: CapabilityOwner;
  reason: string | null;
  /** True when the sentence is the platform speculating, not Stripe answering. */
  guessed: boolean;
}

export interface MethodState {
  key: string;
  label: string;
  note?: string;
  enabled: boolean;
  available: boolean;
  unavailableReason: string | null;
  capability: string | null;
  capabilityStatus: string | null;
  owner: CapabilityOwner;
  guessed: boolean;
}

export interface MethodGroup {
  group: string;
  methods: MethodState[];
}

export interface MethodList {
  configuration_id: string | null;
  groups: MethodGroup[];
  other: MethodState[];
  /**
   * The account itself. When `blocked_reason` is set, nothing below it can run
   * whatever the rows say, so the page states that once and stops decorating
   * every method with a warning about it.
   */
  account: {
    charges_enabled: boolean;
    details_submitted: boolean;
    blocked_reason: string | null;
    requirements_due: string[];
    missing_capabilities: string[];
  };
}

export interface PaymentLinkRow {
  id: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
  revoked: boolean;
  revoked_at: string | null;
  used_at: string | null;
}

// ---------------------------------------------------------------------------
// EU platform reporting (DAC7)
// ---------------------------------------------------------------------------

/**
 * Everything Annex V Section II(B) of Council Directive (EU) 2021/514 asks a
 * platform to hold about one seller, as the panel sees it.
 *
 * `tins` is a list rather than a field because the Directive asks for "any TIN
 * issued to that Seller, including each Member State of issuance" - a seller
 * resident in two Member States has two, and the second one is what makes them
 * resident in the second country.
 */
export interface Dac7Profile {
  id: string;
  merchantId: string;
  sellerType: 'INDIVIDUAL' | 'ENTITY' | 'UNKNOWN';
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  placeOfBirthCountry: string | null;
  legalName: string | null;
  businessRegistrationNumber: string | null;
  permanentEstablishments: string[];
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    region: string | null;
    postCode: string | null;
    country: string | null;
  };
  tins: Array<{ tin: string; issuedBy: string }>;
  vatNumber: string | null;
  financialAccountIdentifier: string | null;
  accountHolderName: string | null;
  stripeExternalAccountLast4: string | null;
  relevantActivities: string[];
  propertyListings: Array<{
    address: string;
    country: string;
    landRegistrationNumber?: string | null;
    daysRented?: number | null;
    propertyType?: string | null;
  }>;
  governmentalEntity: boolean;
  publiclyTradedEntity: boolean;
  exclusionEvidence: string | null;
  dueDiligenceStatus: string;
  collectedAt: string | null;
  verifiedAt: string | null;
  confirmedAt: string | null;
  remindersSent: number;
  lastReminderAt: string | null;
  source: string;
  notes: string | null;
  updatedAt: string;
}

export interface Dac7MissingField {
  field: string;
  label: string;
  citation: string;
  /**
   * `required` blocks a report. `conditional` bites only in a stated
   * circumstance. `where_available` is qualified that way in the Directive and
   * its absence is not a failure - which is why the panel must not render all
   * three the same way.
   */
  severity: 'required' | 'conditional' | 'where_available';
  note?: string;
}

export interface Dac7Guidance {
  relevant_activities: Array<{ key: string; label: string; description: string; citation: string }>;
  member_states: string[];
  de_minimis: {
    activity_count: number;
    consideration_eur: number;
    applies_to: string;
    citation: string;
    note: string;
  };
}

/** Per quarter and per currency. These are never added across currencies. */
export interface Dac7Aggregate {
  merchantId: string;
  year: number;
  quarters: Array<{
    quarter: number;
    currency: string;
    consideration: number;
    gross: number;
    refunded: number;
    feesWithheld: number;
    activityCount: number;
  }>;
  totals: Array<{ currency: string; consideration: number; feesWithheld: number; activityCount: number }>;
  activityCount: number;
}

export interface Dac7Input {
  seller_type?: 'INDIVIDUAL' | 'ENTITY';
  first_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  place_of_birth?: string | null;
  place_of_birth_country?: string | null;
  legal_name?: string | null;
  business_registration_number?: string | null;
  permanent_establishments?: string[];
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    region?: string | null;
    post_code?: string | null;
    country?: string | null;
  };
  tins?: Array<{ tin: string; issued_by: string }>;
  vat_number?: string | null;
  financial_account_identifier?: string | null;
  account_holder_name?: string | null;
  relevant_activities?: Array<
    'IMMOVABLE_PROPERTY_RENTAL' | 'PERSONAL_SERVICE' | 'SALE_OF_GOODS' | 'TRANSPORT_RENTAL'
  >;
}

/** A merchant's own outgoing mail server. The password is never returned. */
export interface MailSettings {
  configured: boolean;
  host: string | null;
  port: number | null;
  secure: boolean;
  username: string | null;
  fromName: string | null;
  fromEmail: string | null;
  replyTo: string | null;
  /** Set only by a test send that actually worked. Null means unproven. */
  verifiedAt: string | null;
  lastTestAt: string | null;
  lastError: string | null;
  /** False when the server holds no encryption key, so nothing can be saved. */
  available: boolean;
}

export interface MailDelivery {
  id: string;
  kind: string;
  to: string;
  subject: string;
  payment_id: string | null;
  status: string;
  error: string | null;
  created_at: string;
}

export interface MachineAccessState {
  status: 'NOT_REQUESTED' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED' | string;
  approved: boolean;
  merchant: { id: string; name: string; active: boolean; stripe_account_id: string | null; charges_enabled: boolean; payouts_enabled: boolean };
  protocols: { mpp: boolean; x402: boolean };
  x402: { pay_to: string | null; network: string | null; token: string | null };
  platform: { enabled: boolean; mpp_enabled: boolean; mpp_live_approved: boolean; x402_live_approved: boolean };
  resource_base_url: string;
  requested_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  updated_at: string;
}

export interface MachineResource {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  method: string;
  protocol: string;
  price_minor: number;
  currency: string;
  network: string | null;
  token: string | null;
  x402_pay_to: string | null;
  status: string;
  livemode: boolean;
  resource_url: string;
  mpp_discovery_url: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Where Tap to Pay may actually be used from this business's country.
 *
 * Answered by the server, from Stripe's published lists. The two platforms are
 * reported separately because they genuinely differ, and an iPhone-only shop in
 * a country supported for Android alone is still stuck - a single "supported"
 * flag would tell that shop the opposite of the truth.
 */
export interface TapToPayAvailability {
  ios: boolean;
  android: boolean;
  /** True when neither platform can be used from this country. */
  unavailable: boolean;
  country: string;
  listsCheckedOn: string;
}

export interface TerminalsPage {
  data: TerminalRow[];
  feature: {
    granted: boolean;
    blocked_reason: string | null;
    requestable: boolean;
    request_status: string | null;
  };
  tap_to_pay: TapToPayAvailability;
}

/**
 * What a merchant needs to register their shop with their tax authority.
 *
 * The processor block is Stripe's, never the platform's, because the licensed
 * payment processor is Stripe. Each processor value is flagged to be confirmed
 * against the merchant's own Stripe invoice, which is the authoritative source.
 */
export interface TaxRegistrationPage {
  guide: 'bg' | 'generic';
  merchant: {
    name: string;
    country: string;
    stripeAccountId: string | null;
    payUrl: string;
  };
  processor: {
    name: string;
    country: string;
    countryCode: string;
    vat: string;
    regNumber: string;
    address: string;
    verifyOnInvoice: boolean;
  };
}

/** A phone registered to take payments in person. */
export interface TerminalRow {
  id: string;
  label: string;
  status: string;
  platform: string | null;
  os_version: string | null;
  app_version: string | null;
  device_model: string | null;
  registered_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  /** The last thing that went wrong on this device, or null if nothing has. */
  last_problem: TerminalProblem | null;
}

/**
 * A failure on a terminal, as the operator was shown it.
 *
 * The wording comes from whichever side knew what happened: the app for
 * anything inside the Terminal SDK, the server for its own refusals. Neither
 * re-words the other, so what appears here is what the person at the counter
 * read off the screen.
 */
export interface TerminalProblem {
  at: string;
  actor: string;
  code: string;
  stage: 'setup' | 'reader' | 'payment' | 'capture' | 'network';
  message: string;
  /** True when the phone itself has to change before it can take payments. */
  permanent: boolean;
}

export interface TerminalProblemsPage {
  terminal: { id: string; label: string };
  data: TerminalProblem[];
}

/**
 * Whether Stripe will sell and activate reader hardware in this country.
 *
 * A separate answer from `TapToPayAvailability` and separately maintained on
 * the server, because the questions are different: one is about the aerial in a
 * handset the merchant already owns, the other about whether Stripe ships a
 * machine here. Mexico has the first and not the second.
 */
export interface ReaderAvailability {
  available: boolean;
  country: string;
  listsCheckedOn: string;
}

export interface ReadersPage {
  data: ReaderRow[];
  feature: {
    granted: boolean;
    blocked_reason: string | null;
    requestable: boolean;
    request_status: string | null;
  };
  readers: ReaderAvailability;
  /** What a sale is priced in unless the merchant says otherwise. */
  currency: string;
}

/** A Stripe smart reader on a counter. */
export interface ReaderRow {
  id: string;
  label: string;
  status: string;
  device_type: string | null;
  serial_number: string | null;
  /** `online` or `offline`, as Stripe last saw it. Null when never reported. */
  connection_status: string | null;
  registered_at: string;
  last_seen_at: string | null;
  removed_at: string | null;
  removed_reason: string | null;
}

/** What the machine on the counter is doing, straight from Stripe. */
export interface ReaderAction {
  type: string | null;
  status: string | null;
  failure_code: string | null;
  failure_message: string | null;
}

export interface ReaderPaymentStarted {
  payment_id: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  action: ReaderAction;
}

export interface ReaderPaymentProgress {
  payment_id: string;
  /** WyoCSPay's own state - the one the ledger and the reports use. */
  state: string;
  action: ReaderAction;
  intent_status: string | null;
  amount: number;
  /** What the customer added on the device, when the prompt was shown. */
  tip: number;
  currency: string;
}
